/** Earth mean radius in meters (WGS84 spherical approximation). */
const EARTH_RADIUS_METERS = 6_371_000;

export type LatLng = {
  latitude: number;
  longitude: number;
};

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Great-circle distance between two WGS84 points using the haversine formula.
 * Returns meters. Invalid/non-finite coordinates yield `Number.POSITIVE_INFINITY`.
 */
export function distanceMeters(a: LatLng, b: LatLng): number {
  if (
    !Number.isFinite(a.latitude) ||
    !Number.isFinite(a.longitude) ||
    !Number.isFinite(b.latitude) ||
    !Number.isFinite(b.longitude)
  ) {
    return Number.POSITIVE_INFINITY;
  }

  const lat1 = toRadians(a.latitude);
  const lat2 = toRadians(b.latitude);
  const dLat = lat2 - lat1;
  const dLng = toRadians(b.longitude - a.longitude);

  const sinHalfLat = Math.sin(dLat / 2);
  const sinHalfLng = Math.sin(dLng / 2);
  const h =
    sinHalfLat * sinHalfLat +
    Math.cos(lat1) * Math.cos(lat2) * sinHalfLng * sinHalfLng;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return EARTH_RADIUS_METERS * c;
}

/** True when two points are within `thresholdMeters` (inclusive). */
export function isWithinMeters(
  a: LatLng,
  b: LatLng,
  thresholdMeters: number,
): boolean {
  return distanceMeters(a, b) <= thresholdMeters;
}
